import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  deleteSavedTesterStrategy,
  exportSavedTesterStrategyEa,
  listSavedTesterStrategies,
} from './strategyTesterDataFeeds';
import PayPalSubscriptionCard from './PayPalSubscriptionCard';
import SignalsTab from './SignalsTab';
import LiveTrackingTab from './LiveTrackingTab';
import EmailSubscriptionsPanel from './EmailSubscriptionsPanel';
import './UserDashboard.css';
import './SignalsTab.css';
import './LiveAccountPage.css';
import { buildDownloadUrl } from './downloadUrl';

const PREMIUM_PRICE_LABEL = '$2.99/month';

function downloadTextFile(filename, content) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function UserDashboard() {
  const [tab, setTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [savedStrategies, setSavedStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [strategyFeedback, setStrategyFeedback] = useState('');
  const [strategyActionId, setStrategyActionId] = useState('');
  const [profileName, setProfileName] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordFeedback, setPasswordFeedback] = useState('');
  const [passwordPending, setPasswordPending] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [toast, setToast] = useState('');
  const [myReviews, setMyReviews] = useState([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isRefreshingChat, setIsRefreshingChat] = useState(false);
  const [showClosedChats, setShowClosedChats] = useState(false);
  const [purchaseFeedback, setPurchaseFeedback] = useState('');
  const [licenseActionId, setLicenseActionId] = useState(null);
  // Inline activation-code form, per purchase. Held here rather than in a browser dialog so the
  // page keeps control of the interaction.
  const [activationFor, setActivationFor] = useState(null);
  const [activationAccount, setActivationAccount] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [activationError, setActivationError] = useState('');
  const [activationBusy, setActivationBusy] = useState(false);
  const [billingProfile, setBillingProfile] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingAction, setBillingAction] = useState('');
  const [billingFeedback, setBillingFeedback] = useState('');
  const lastAdminMessageIdRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const dashboardNavShellRef = useRef(null);
  const dashboardNavRef = useRef(null);
  const [isDashboardNavPinned, setIsDashboardNavPinned] = useState(false);
  const [isDashboardNavMobile, setIsDashboardNavMobile] = useState(false);
  const [dashboardNavStyle, setDashboardNavStyle] = useState({});
  const [dashboardNavShellHeight, setDashboardNavShellHeight] = useState(null);
  const [dashboardConfirm, setDashboardConfirm] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const openDashboardTab = useCallback((nextTab) => {
    setTab(nextTab);
    navigate(`${location.pathname}?tab=${nextTab}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, navigate]);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'strategies', label: 'Strategies' },
    { id: 'signals', label: 'Signals' },
    { id: 'live', label: 'Live MT5' },
    { id: 'architect', label: 'VixAI' },
    { id: 'community', label: 'Community' },
    { id: 'billing', label: 'Billing' },
    { id: 'licenses', label: 'Licenses' },
    { id: 'purchases', label: 'Purchases' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'support', label: 'Support' },
    { id: 'settings', label: 'Settings' },
  ];

  const unreadSupport = conversations.reduce((sum, convo) => sum + (convo.unreadCount || 0), 0);
  const hasOpenConversation = conversations.some((conversation) => conversation.status === 'open');
  const visibleConversations = conversations.filter((conversation) => conversation.status !== 'closed');
  const closedConversations = conversations.filter((conversation) => conversation.status === 'closed');
  const openChatCount = conversations.filter((conversation) => conversation.status === 'open').length;
  const currentSubscription = billingProfile?.subscription || null;
  const premiumPlan = billingProfile?.plans?.find((plan) => plan.code === 'premium') || null;
  const premiumPriceLabel = premiumPlan?.priceLabel && premiumPlan.priceLabel !== 'Monthly subscription'
    ? premiumPlan.priceLabel
    : PREMIUM_PRICE_LABEL;
  const hasActivePremium = user?.testerPlan === 'premium';

  const openDashboardConfirm = useCallback((nextConfirm) => {
    setDashboardConfirm({
      title: 'Confirm Action',
      message: '',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      tone: '',
      ...nextConfirm,
    });
  }, []);

  const closeDashboardConfirm = useCallback(() => {
    setDashboardConfirm(null);
  }, []);

  const syncUserProfile = useCallback((nextUser) => {
    if (!nextUser) {
      return;
    }
    setUser(nextUser);
    setProfileName(nextUser.name || '');
    localStorage.setItem('userProfile', JSON.stringify(nextUser));
    window.dispatchEvent(new Event('auth-changed'));
  }, []);

  const loadEntitlements = useCallback(async () => {
    const token = localStorage.getItem('userToken');
    if (!token) return;
    const [licenseResponse, purchaseResponse] = await Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL}/api/user/licenses`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${process.env.REACT_APP_API_URL}/api/user/purchases`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    setLicenses(Array.isArray(licenseResponse.data) ? licenseResponse.data : []);
    const refreshedPurchases = Array.isArray(purchaseResponse.data) ? purchaseResponse.data : [];
    setPurchases(refreshedPurchases);
    // Returned as well as stored: a caller that has just created a licence needs the new key
    // immediately, and React state is not readable until the next render.
    return refreshedPurchases;
  }, []);

  const refreshBillingProfile = useCallback(async ({ syncRemote = false, silent = false } = {}) => {
    const token = localStorage.getItem('userToken');
    if (!token) {
      return null;
    }
    if (!silent) {
      setBillingLoading(true);
    }
    try {
      const response = syncRemote
        ? await axios.post(
          `${process.env.REACT_APP_API_URL}/api/billing/paypal/sync`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        )
        : await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      setBillingProfile(response.data);
      if (response.data?.user) {
        syncUserProfile(response.data.user);
      }
      return response.data;
    } catch (err) {
      setError('Failed to load billing details.');
      return null;
    } finally {
      if (!silent) {
        setBillingLoading(false);
      }
    }
  }, [syncUserProfile]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    const validTabs = ['overview', 'strategies', 'signals', 'live', 'billing', 'licenses', 'purchases', 'reviews', 'support', 'settings'];
    if (requestedTab && validTabs.includes(requestedTab)) {
      setTab(requestedTab);
    }
  }, [location.search]);

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const meResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        syncUserProfile(meResponse.data.user);
        await loadEntitlements();
        const [convoResponse, reviewResponse, strategyResponse, billingResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/chat/conversations`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/user/reviews`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          listSavedTesterStrategies(token),
          axios.get(`${process.env.REACT_APP_API_URL}/api/billing/me`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setConversations(Array.isArray(convoResponse.data) ? convoResponse.data : []);
        setMyReviews(Array.isArray(reviewResponse.data) ? reviewResponse.data : []);
        setSavedStrategies(Array.isArray(strategyResponse.strategies) ? strategyResponse.strategies : []);
        setBillingProfile(billingResponse.data || null);
        if (billingResponse.data?.user) {
          syncUserProfile(billingResponse.data.user);
        }
      } catch (err) {
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [loadEntitlements, navigate, syncUserProfile]);

  const handleSignOut = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userProfile');
    window.dispatchEvent(new Event('auth-changed'));
    navigate('/login');
  };

  const handleProfileSave = async () => {
    const token = localStorage.getItem('userToken');
    if (!token) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        { name: profileName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      syncUserProfile({ ...(user || {}), name: profileName });
    } catch (err) {
      setError('Failed to update profile.');
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    const token = localStorage.getItem('userToken');
    if (!token) return;
    setPasswordFeedback('');
    setError('');
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setPasswordPending(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/change-password`,
        {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordFeedback('Password updated.');
      setShowPasswordForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password.');
    } finally {
      setPasswordPending(false);
    }
  };

  const handleDeleteAccount = async () => {
    const token = localStorage.getItem('userToken');
    if (!token) return;
    openDashboardConfirm({
      title: 'Delete Account',
      message: 'This removes your profile. Purchase records remain for accounting.',
      confirmLabel: 'Delete Account',
      tone: 'danger',
      onConfirm: async () => {
        closeDashboardConfirm();
        try {
          await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          localStorage.removeItem('userToken');
          localStorage.removeItem('userProfile');
          window.dispatchEvent(new Event('auth-changed'));
          navigate('/');
        } catch (err) {
          setError('Failed to delete account.');
        }
      },
    });
  };

  const refreshSavedStrategies = useCallback(async () => {
    const token = localStorage.getItem('userToken');
    if (!token) return;
    const response = await listSavedTesterStrategies(token);
    setSavedStrategies(Array.isArray(response.strategies) ? response.strategies : []);
  }, []);

  const handleOpenStrategyInTester = (strategyId) => {
    navigate(`/tester?strategy=${strategyId}`);
  };

  const handleExportStrategy = async (strategyId) => {
    const token = localStorage.getItem('userToken');
    if (!token) return;
    setStrategyActionId(`export:${strategyId}`);
    setStrategyFeedback('');
    try {
      const response = await exportSavedTesterStrategyEa(strategyId, token);
      downloadTextFile(response.filename, response.content);
      setStrategyFeedback('EA source exported.');
    } catch (err) {
      setError('Failed to export strategy.');
    } finally {
      setStrategyActionId('');
    }
  };

  const handleDeleteStrategy = async (strategyId) => {
    const token = localStorage.getItem('userToken');
    if (!token) return;
    openDashboardConfirm({
      title: 'Delete Strategy',
      message: 'Delete this saved Builder strategy?',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        closeDashboardConfirm();
        setStrategyActionId(`delete:${strategyId}`);
        setStrategyFeedback('');
        try {
          await deleteSavedTesterStrategy(strategyId, token);
          await refreshSavedStrategies();
          setStrategyFeedback('Strategy deleted.');
        } catch (err) {
          setError('Failed to delete strategy.');
        } finally {
          setStrategyActionId('');
        }
      },
    });
  };

  const handleApproveSubscription = async (data) => {
    const token = localStorage.getItem('userToken');
    if (!token) {
      navigate('/login');
      return;
    }
    setBillingAction('subscribe');
    setBillingFeedback('');
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/billing/paypal/activate`,
        { subscriptionId: data.subscriptionID },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBillingProfile((prev) => ({
        ...(prev || {}),
        ...(response.data || {}),
        plans: response.data?.plans || prev?.plans || [],
      }));
      if (response.data?.user) {
        syncUserProfile(response.data.user);
      }
      await refreshBillingProfile({ silent: true });
      setBillingFeedback('VixPremium subscription activated.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate the PayPal subscription.');
    } finally {
      setBillingAction('');
    }
  };

  const handleCancelSubscription = async () => {
    const token = localStorage.getItem('userToken');
    if (!token) return;
    const cancelMessage = currentSubscription?.isTrialing
      ? 'Cancel your VixPremium subscription? You will lose access to all premium benefits.'
      : 'Cancel your VixPremium subscription? You will keep premium access until the end of your current billing period, after which it will not renew.';
    openDashboardConfirm({
      title: 'Cancel VixPremium',
      message: cancelMessage,
      confirmLabel: 'Cancel Subscription',
      tone: 'danger',
      onConfirm: async () => {
        closeDashboardConfirm();
        setBillingAction('cancel');
        setBillingFeedback('');
        setError('');
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/billing/paypal/cancel`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setBillingProfile((prev) => ({
            ...(prev || {}),
            ...(response.data || {}),
            plans: response.data?.plans || prev?.plans || [],
          }));
          if (response.data?.user) {
            syncUserProfile(response.data.user);
          }
          await refreshBillingProfile({ silent: true });
          setBillingFeedback('VixPremium subscription cancelled.');
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to cancel the PayPal subscription.');
        } finally {
          setBillingAction('');
        }
      },
    });
  };

  const refreshConversations = useCallback(async () => {
    const token = localStorage.getItem('userToken');
    if (!token) return;
    try {
      setIsRefreshingChat(true);
      const convoResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(convoResponse.data) ? convoResponse.data : [];
      setConversations(list);
      if (activeConversation?.id) {
        const matchedConversation = list.find((conversation) => conversation.id === activeConversation.id);
        if (matchedConversation) {
          setActiveConversation(matchedConversation);
        } else {
          setActiveConversation(null);
          setMessages([]);
        }
      }
      return list;
    } catch (err) {
      setError('Failed to load chat conversations.');
    } finally {
      setIsRefreshingChat(false);
    }
  }, [activeConversation]);

  const loadMessages = useCallback(async (conversationId) => {
    const token = localStorage.getItem('userToken');
    if (!token) return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/chat/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newMessages = Array.isArray(response.data) ? response.data : [];
      setMessages(newMessages);
      const lastAdminMessage = [...newMessages].reverse().find((msg) => msg.senderRole === 'admin');
      if (lastAdminMessage) {
        if (lastAdminMessageIdRef.current && lastAdminMessage.id > lastAdminMessageIdRef.current) {
          setToast('New reply received');
          setTimeout(() => setToast(''), 3000);
        }
        lastAdminMessageIdRef.current = lastAdminMessage.id;
      }
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/chat/conversations/${conversationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setError('Failed to load chat messages.');
    }
  }, []);

  const handleConversationSelect = (conversation) => {
    setShowClosedChats(false);
    setActiveConversation(conversation);
    lastAdminMessageIdRef.current = null;
    loadMessages(conversation.id);
  };

  const handleStartNewConversation = () => {
    setActiveConversation(null);
    setMessages([]);
    setNewSubject('');
    setMessageText('');
    setAttachments([]);
    lastAdminMessageIdRef.current = null;
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    const token = localStorage.getItem('userToken');
    if (!token) return;
    if ((!messageText && attachments.length === 0) || isSendingMessage) return;
    try {
      setIsSendingMessage(true);
      const formData = new FormData();
      if (activeConversation?.id && activeConversation.status !== 'closed') {
        formData.append('conversationId', activeConversation.id);
      }
      if (newSubject && (!activeConversation?.id || activeConversation.status === 'closed')) {
        formData.append('subject', newSubject);
      }
      formData.append('message', messageText);
      attachments.forEach((file) => formData.append('attachments', file));
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/chat/messages`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessageText('');
      setAttachments([]);
      setNewSubject('');
      const updated = await refreshConversations();
      const convoId = response.data?.conversationId || activeConversation?.id;
      if (convoId) {
        const convo = updated?.find((c) => c.id === convoId) || activeConversation;
        setActiveConversation(convo || null);
        loadMessages(convoId);
      }
    } catch (err) {
      setError('Failed to send message.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleGenerateLicenseForPurchase = async (purchase) => {
    const resolvedEmail = purchase.email || user?.email;
    if (!resolvedEmail) {
      setError('Missing purchase email for this order.');
      return;
    }

    try {
      setLicenseActionId(purchase.id);
      setPurchaseFeedback('');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/licenses`, {
        purchaseId: purchase.id,
        email: resolvedEmail,
      });
      await loadEntitlements();
      setPurchaseFeedback(`License ready for ${purchase.productName || 'this purchase'}.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create license.');
    } finally {
      setLicenseActionId(null);
    }
  };

  // Indicators cannot verify a licence over the network — MT5 refuses WebRequest inside an
  // indicator — so they use an activation code bound to one MT5 account, checked locally.
  //
  // The account is collected by an inline form rather than a browser prompt: a native dialog is
  // unstyled, blocks the page, and on mobile is easy to dismiss by accident.
  const openActivationForm = (purchase) => {
    setActivationFor(purchase.id);
    setActivationAccount('');
    setActivationCode('');
    setActivationError('');
  };

  const closeActivationForm = () => {
    setActivationFor(null);
    setActivationAccount('');
    setActivationCode('');
    setActivationError('');
  };

  const handleGetActivationCode = async (purchase) => {
    const digits = String(activationAccount).replace(/[^0-9]/g, '');
    if (!digits) {
      setActivationError('Enter the MT5 account number (digits only).');
      return;
    }

    try {
      setActivationBusy(true);
      setActivationError('');

      // The licence is what proves ownership, so create it first if this purchase has none.
      // Previously the button read "Create License" until that had happened, which meant an
      // indicator buyer was told to do a step that produces a key their indicator cannot use.
      let { licenseKey } = purchase;
      if (!licenseKey) {
        const resolvedEmail = purchase.email || user?.email;
        await axios.post(`${process.env.REACT_APP_API_URL}/api/licenses`, {
          purchaseId: purchase.id,
          email: resolvedEmail,
        });
        const refreshed = await loadEntitlements();
        licenseKey = refreshed?.find?.((row) => row.id === purchase.id)?.licenseKey || null;
        if (!licenseKey) {
          setActivationError('Could not prepare the license for this purchase. Please try again.');
          return;
        }
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/licenses/offline-code`,
        { licenseKey, accountNumber: digits },
        { headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` } },
      );
      setActivationCode(response.data.code);
      setActivationAccount(response.data.accountNumber || digits);
    } catch (err) {
      setActivationError(err.response?.data?.error || 'Failed to create activation code.');
    } finally {
      setActivationBusy(false);
    }
  };

  const handleCopyText = async (value, successMessage) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setPurchaseFeedback(successMessage);
    } catch (err) {
      setError('Failed to copy to clipboard.');
    }
  };

  useEffect(() => {
    if (tab !== 'support') return;
    const intervalId = setInterval(async () => {
      await refreshConversations();
      if (activeConversation?.id) {
        loadMessages(activeConversation.id);
      }
    }, 15000);
    return () => clearInterval(intervalId);
  }, [tab, activeConversation, loadMessages, refreshConversations]);

  useEffect(() => {
    const updateDashboardNavPosition = () => {
      if (!dashboardNavShellRef.current || !dashboardNavRef.current) return;

      const isMobile = window.innerWidth <= 960;
      const topOffset = isMobile ? 92 : 108;
      const shellRect = dashboardNavShellRef.current.getBoundingClientRect();
      const navHeight = dashboardNavRef.current.offsetHeight;
      const shouldPin = shellRect.top <= topOffset;

      setIsDashboardNavMobile(isMobile);
      setDashboardNavShellHeight(navHeight);

      if (shouldPin) {
        setDashboardNavStyle({
          left: `${shellRect.left}px`,
          width: `${shellRect.width}px`,
          top: `${topOffset}px`,
        });
      } else {
        setDashboardNavStyle({});
      }

      setIsDashboardNavPinned(shouldPin);
    };

    updateDashboardNavPosition();
    window.addEventListener('scroll', updateDashboardNavPosition, { passive: true });
    window.addEventListener('resize', updateDashboardNavPosition);

    return () => {
      window.removeEventListener('scroll', updateDashboardNavPosition);
      window.removeEventListener('resize', updateDashboardNavPosition);
    };
  }, []);

  return (
    <div className="dashboard page-shell">
      <div className="dashboard-header">
        <div>
          <span className="tag">Account</span>
          <h1 className="page-title">Your dashboard</h1>
          <p className="page-lead">Manage licenses, support, and profile settings.</p>
        </div>
        <button className="ghost-button" onClick={handleSignOut}>Sign out</button>
      </div>

      <div className="dashboard-layout">
        <aside
          className={`dashboard-nav-shell ${isDashboardNavPinned ? 'pinned' : ''}`}
          ref={dashboardNavShellRef}
          style={isDashboardNavPinned && dashboardNavShellHeight ? { height: `${dashboardNavShellHeight}px` } : undefined}
        >
          <div
            ref={dashboardNavRef}
            className={`dashboard-nav ${tab === 'support' ? 'support-active' : ''} ${isDashboardNavPinned ? 'pinned' : ''} ${isDashboardNavMobile ? 'mobile-fixed' : ''}`}
            style={isDashboardNavPinned ? dashboardNavStyle : undefined}
            >
              {tabs.map((item) => (
                <button
                  key={item.id}
                  className={`dashboard-tab ${tab === item.id ? 'active' : ''}`}
                  onClick={() => openDashboardTab(item.id)}
                  type="button"
                >
                  {item.label}
                {item.id === 'support' && unreadSupport > 0 && (
                  <span className="badge">{unreadSupport}</span>
                )}
              </button>
            ))}
          </div>
        </aside>

        <section className="dashboard-content">
          {error && <div className="section-card">{error}</div>}
          {loading && <div className="section-card">Loading dashboard...</div>}
          {tab === 'overview' && (
            <div className="dashboard-grid">
                <div className="stat-card">
                  <p className="stat-label">VixPremium plan</p>
                  <h3>{user?.testerPlan === 'premium' ? 'VixPremium' : 'Free'}</h3>
                  <p className="muted-text">
                    {user?.testerPlan === 'premium'
                      ? `Live signals, MT5 copying, EA imports, live account tracking, up to ${user?.testerStrategyLimit || 100} saved strategies, MQ5 export, and Community access.`
                      : `Up to ${user?.testerStrategyLimit || 1} saved strategy and three MQ5 exports per month. Live signals, MT5 copying, and EA imports need VixPremium.`}
                  </p>
                </div>
                <div className="stat-card">
                  <p className="stat-label">Subscription</p>
                  <h3>{currentSubscription?.status || 'None'}</h3>
                  <p className="muted-text">
                    {currentSubscription
                      ? `PayPal ${currentSubscription.planCode} subscription linked to this account.`
                      : 'No active VixPremium subscription linked yet.'}
                  </p>
                </div>
                <div className="stat-card">
                  <p className="stat-label">Saved strategies</p>
                <h3>{savedStrategies.length}</h3>
                <p className="muted-text">Open them in the builder or export them as MQL5 EAs.</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Active licenses</p>
                <h3>{licenses.length}</h3>
                <p className="muted-text">Your licenses appear here as they are issued.</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Purchases</p>
                <h3>{purchases.length}</h3>
                <p className="muted-text">Track receipts and download history.</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Open chats</p>
                <h3>{openChatCount}</h3>
                <p className="muted-text">Your support threads will be listed here.</p>
              </div>
            </div>
          )}

          {tab === 'strategies' && (
            <div className="section-card">
              <h3>Saved strategies</h3>
              <p className="muted-text">Open saved builder presets, export MQ5 EAs, or remove old versions.</p>
              <p className="muted-text">
                Plan: {user?.testerPlan === 'premium' ? 'VixPremium' : 'Free'} · Save limit {user?.testerStrategyLimit || 1}
                {user?.canExportMq5 ? ' · Unlimited MQ5 export' : ' · 3 MQ5 exports per month'}
              </p>
              {strategyFeedback && <p className="success-text">{strategyFeedback}</p>}
                <div className="inline-actions">
                  <button type="button" className="primary-cta compact-button" onClick={() => navigate('/tester')}>
                    Open Builder
                  </button>
                  <button type="button" className="ghost-button compact-button" onClick={refreshSavedStrategies}>
                    Refresh
                    </button>
                    {user?.testerPlan !== 'premium' ? (
                      <button type="button" className="ghost-button compact-button" onClick={() => openDashboardTab('billing')}>
                        Upgrade
                      </button>
                    ) : null}
                </div>
              <div className="table-shell">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Symbol</th>
                      <th>Timeframe</th>
                      <th>Source</th>
                      <th>Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedStrategies.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="empty-cell">No saved strategies yet.</td>
                      </tr>
                    ) : (
                      savedStrategies.map((strategy) => (
                        <tr key={strategy.id}>
                          <td>
                            <strong>{strategy.name}</strong>
                            {strategy.description ? <p className="muted-text">{strategy.description}</p> : null}
                          </td>
                          <td>{strategy.symbol || '-'}</td>
                          <td>{strategy.timeframe || '-'}</td>
                          <td>{strategy.dataSource || '-'}</td>
                          <td>{new Date(strategy.updatedAt || strategy.createdAt).toLocaleDateString()}</td>
                          <td>
                            <div className="inline-actions">
                              <button
                                type="button"
                                className="primary-cta compact-button"
                                onClick={() => handleOpenStrategyInTester(strategy.id)}
                              >
                                Open
                              </button>
                              <button
                                type="button"
                                className="ghost-button compact-button"
                                onClick={() => handleExportStrategy(strategy.id)}
                                disabled={strategyActionId === `export:${strategy.id}` || !user?.canExportMq5}
                              >
                                {strategyActionId === `export:${strategy.id}` ? 'Exporting...' : 'Export MQ5'}
                              </button>
                              <button
                                type="button"
                                className="ghost-button compact-button"
                                onClick={() => handleDeleteStrategy(strategy.id)}
                                disabled={strategyActionId === `delete:${strategy.id}`}
                              >
                                {strategyActionId === `delete:${strategy.id}` ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'signals' && (
            <SignalsTab
              user={user}
              savedStrategies={savedStrategies}
              onUpgrade={() => openDashboardTab('billing')}
            />
          )}

          {tab === 'live' && (
            <LiveTrackingTab savedStrategies={savedStrategies} />
          )}

          {tab === 'architect' && (
            <div className="section-card">
              <h3>VixAI Strategy Agent</h3>
              <p className="muted-text">
                Chat with VixAI to draft custom non-indicator logic, custom inputs, chart visuals, and info panels as controlled VixAlgoBuilder DSL specs.
              </p>
              <div className="dashboard-grid dashboard-grid-tight">
                <div className="stat-card">
                  <p className="stat-label">Access</p>
                  <h3>{hasActivePremium ? 'Active' : 'Locked'}</h3>
                  <p className="muted-text">
                    {hasActivePremium
                      ? 'VixAI chat, prompt-to-DSL drafts, validation, and draft storage are enabled.'
                      : 'Activate VixPremium to use VixAI strategy drafts.'}
                  </p>
                </div>
              </div>
              <div className="inline-actions">
                <button type="button" className="primary-cta compact-button" onClick={() => navigate('/vixai')}>
                  Open VixAI
                </button>
                <button type="button" className="ghost-button compact-button" onClick={() => navigate('/builder-docs')}>
                  Read DSL Docs
                </button>
                {!hasActivePremium ? (
                  <button type="button" className="ghost-button compact-button" onClick={() => openDashboardTab('billing')}>
                    Activate VixPremium
                  </button>
                ) : null}
              </div>
            </div>
          )}

          {tab === 'community' && (
            <div className="section-card">
              <h3>VixPremium Community</h3>
              <p className="muted-text">
                Share strategy threads, MT5 reports, images, PDFs, videos, files, and builder presets with other VixPremium members.
              </p>
              <div className="dashboard-grid dashboard-grid-tight">
                <div className="stat-card">
                  <p className="stat-label">Access</p>
                  <h3>{hasActivePremium ? 'Active' : 'Locked'}</h3>
                  <p className="muted-text">
                    {hasActivePremium
                      ? 'Community posting, downloads, comments, and strategy imports are enabled.'
                      : 'Activate VixPremium to join the community and import public strategies.'}
                  </p>
                </div>
                <div className="stat-card">
                  <p className="stat-label">Strategy vault</p>
                  <h3>{savedStrategies.length}</h3>
                  <p className="muted-text">Saved strategies can be shared as portable community snapshots.</p>
                </div>
              </div>
              <div className="inline-actions">
                <button type="button" className="primary-cta compact-button" onClick={() => navigate('/community')}>
                  Open Community
                </button>
                <button type="button" className="ghost-button compact-button" onClick={() => navigate('/builder-docs')}>
                  Read Builder Docs
                </button>
                {!hasActivePremium ? (
                  <button type="button" className="ghost-button compact-button" onClick={() => openDashboardTab('billing')}>
                    Activate VixPremium
                  </button>
                ) : null}
              </div>
            </div>
          )}

          {tab === 'licenses' && (
            <div className="section-card">
              <h3>Licenses</h3>
              <p className="muted-text">Manage device-bound licenses per product.</p>
              <div className="table-shell">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>License</th>
                      <th>Devices</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {licenses.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="empty-cell">No licenses yet.</td>
                      </tr>
                    ) : (
                      licenses.map((license) => (
                        <tr key={license.id}>
                          <td>{license.productName || 'Product'}</td>
                          <td>{license.licenseKey}</td>
                          <td>{license.deviceCount}/{license.deviceLimit}</td>
                          <td>{license.status}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'purchases' && (
            <div className="section-card">
              <h3>Purchase history</h3>
              <p className="muted-text">Access product files and create licenses from your orders.</p>
              {purchaseFeedback && <p className="success-text">{purchaseFeedback}</p>}
              <div className="table-shell">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Amount</th>
                      <th>Order ID</th>
                      <th>License</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="empty-cell">No purchases found.</td>
                      </tr>
                    ) : (
                      purchases.map((purchase) => (
                        <tr key={purchase.id}>
                          <td>{new Date(purchase.createdAt).toLocaleDateString()}</td>
                          <td>{purchase.productName || purchase.productId}</td>
                          <td>${Number(purchase.amountPaid || 0).toFixed(2)}</td>
                          <td className="license-key">{purchase.orderId}</td>
                          <td>
                            {purchase.licenseKey ? (
                              <span className="status-chip active">
                                {purchase.deviceCount || 0}/{purchase.deviceLimit || 2} bound
                              </span>
                            ) : (
                              <span className="status-chip pending">Not created</span>
                            )}
                          </td>
                          <td>
                            <div className="inline-actions">
                              {purchase.productType === 'indicator' ? (
                                // Indicators ALWAYS offer the activation code, whether or not a
                                // licence row exists yet — the handler creates one if needed. The
                                // old two-step ("Create License", then the code) asked an indicator
                                // buyer to produce a key their indicator cannot use.
                                <button
                                  type="button"
                                  className="ghost-button compact-button"
                                  onClick={() => openActivationForm(purchase)}
                                  disabled={activationFor === purchase.id}
                                  title="Indicators activate with a code tied to one MT5 account"
                                >
                                  Get Activation Code
                                </button>
                              ) : purchase.licenseKey ? (
                                <button
                                  type="button"
                                  className="ghost-button compact-button"
                                  onClick={() => handleCopyText(purchase.licenseKey, 'License key copied.')}
                                >
                                  Copy License
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="ghost-button compact-button"
                                  onClick={() => handleGenerateLicenseForPurchase(purchase)}
                                  disabled={licenseActionId === purchase.id}
                                >
                                  {licenseActionId === purchase.id ? 'Creating...' : 'Create License'}
                                </button>
                              )}
                              {purchase.filePath && (
                                <a
                                  href={buildDownloadUrl(purchase.filePath)}
                                  className="primary-cta compact-button"
                                  download
                                >
                                  Download
                                </a>
                              )}
                            </div>
                            {activationFor === purchase.id && (
                              <div className="activation-panel">
                                {!activationCode ? (
                                  <>
                                    <p className="activation-hint">
                                      Enter the MT5 account number you will run this indicator on.
                                      The code only works on that account — you can request another
                                      any time if you switch.
                                    </p>
                                    <div className="activation-row">
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        placeholder="MT5 account number"
                                        value={activationAccount}
                                        onChange={(e) => setActivationAccount(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleGetActivationCode(purchase);
                                        }}
                                        autoFocus
                                      />
                                      <button
                                        type="button"
                                        className="primary-cta compact-button"
                                        onClick={() => handleGetActivationCode(purchase)}
                                        disabled={activationBusy}
                                      >
                                        {activationBusy ? 'Working...' : 'Generate'}
                                      </button>
                                      <button
                                        type="button"
                                        className="ghost-button compact-button"
                                        onClick={closeActivationForm}
                                        disabled={activationBusy}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <p className="activation-hint">
                                      Activation code for account <strong>{activationAccount}</strong>.
                                      Paste it into the indicator&apos;s Activation Code input — it
                                      works offline, so no internet permission is needed.
                                    </p>
                                    <div className="activation-row">
                                      <code className="activation-code">{activationCode}</code>
                                      <button
                                        type="button"
                                        className="primary-cta compact-button"
                                        onClick={() => handleCopyText(activationCode, 'Activation code copied.')}
                                      >
                                        Copy
                                      </button>
                                      <button
                                        type="button"
                                        className="ghost-button compact-button"
                                        onClick={closeActivationForm}
                                      >
                                        Done
                                      </button>
                                    </div>
                                  </>
                                )}
                                {activationError && (
                                  <p className="activation-error">{activationError}</p>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'reviews' && (
            <div className="section-card">
              <h3>Your reviews</h3>
              <p className="muted-text">Track reviews you have submitted and their status.</p>
              <div className="table-shell">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Rating</th>
                      <th>Status</th>
                      <th>Title</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myReviews.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="empty-cell">No reviews submitted yet.</td>
                      </tr>
                    ) : (
                      myReviews.map((review) => (
                        <tr key={review.id}>
                          <td>{new Date(review.createdAt).toLocaleDateString()}</td>
                          <td>{review.productName || review.productId}</td>
                          <td>{review.rating}</td>
                          <td>{review.status}</td>
                          <td>{review.title || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'support' && (
            <div className="section-card">
              <div className="support-header">
                <div>
                  <h3>Support conversations</h3>
                  <p className="muted-text">Chat with the team and upload files if needed.</p>
                </div>
                <button type="button" className="ghost-button" onClick={refreshConversations}>
                  {isRefreshingChat ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
              <div className="support-layout">
                <div className="support-list">
                  <input
                    type="text"
                    placeholder="New chat subject"
                    value={newSubject}
                    onChange={(event) => setNewSubject(event.target.value)}
                    disabled={hasOpenConversation || isSendingMessage}
                  />
                  <div className="support-threads">
                    {conversations.length === 0 && <p className="muted-text">No conversations yet.</p>}
                    {visibleConversations.map((convo) => (
                      <button
                        key={convo.id}
                        type="button"
                        className={`support-thread ${activeConversation?.id === convo.id ? 'active' : ''}`}
                        onClick={() => handleConversationSelect(convo)}
                      >
                        <div>
                          <p className="thread-title">{convo.subject || 'Support'}</p>
                          <p className="thread-meta">{convo.status}</p>
                        </div>
                        {convo.unreadCount > 0 && <span className="badge">{convo.unreadCount}</span>}
                      </button>
                    ))}
                    {closedConversations.length > 0 && (
                      <>
                        <button
                          type="button"
                          className="ghost-button chat-toggle-button"
                          onClick={() => setShowClosedChats((current) => !current)}
                        >
                          {showClosedChats ? 'Hide Other Chats' : `Show Other Chats (${closedConversations.length})`}
                        </button>
                        {showClosedChats && closedConversations.map((convo) => (
                          <button
                            key={convo.id}
                            type="button"
                            className={`support-thread ${activeConversation?.id === convo.id ? 'active' : ''}`}
                            onClick={() => handleConversationSelect(convo)}
                          >
                            <div>
                              <p className="thread-title">{convo.subject || 'Support'}</p>
                              <p className="thread-meta">{convo.status}</p>
                            </div>
                            {convo.unreadCount > 0 && <span className="badge">{convo.unreadCount}</span>}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
                <div className="support-chat">
                  {activeConversation ? (
                    <>
                      <div className="chat-header">
                        <div>
                          <h4>{activeConversation.subject || 'Support'}</h4>
                          <p className="muted-text">Status: {activeConversation.status}</p>
                        </div>
                        <div className="support-chat-actions">
                          {activeConversation.status === 'closed' && (
                            <button type="button" className="ghost-button" onClick={handleStartNewConversation}>
                              Start New Issue
                            </button>
                          )}
                          {toast && <span className="support-toast">{toast}</span>}
                        </div>
                      </div>
                      <div className="chat-messages">
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`chat-row ${msg.senderRole === 'user' ? 'outgoing' : 'incoming'}`}
                          >
                            <div className={`chat-bubble ${msg.senderRole}`}>
                              {msg.message && <p>{msg.message}</p>}
                              {msg.attachments?.length > 0 && (
                                <div className="chat-attachments">
                                  {msg.attachments.map((att) => (
                                    <a
                                      key={att.id}
                                      href={`${process.env.REACT_APP_API_URL}/${att.filePath}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {att.originalName || 'Attachment'}
                                    </a>
                                  ))}
                                </div>
                              )}
                              <div className="message-footer">
                                <span className="message-time">
                                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {msg.senderRole === 'user' && (
                                  <span className="read-receipt">
                                    {msg.readAtAdmin ? String.fromCharCode(10003, 10003) : String.fromCharCode(10003)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="support-empty">
                      <p>Select a conversation or start a new one.</p>
                    </div>
                  )}
                  <form className="support-input" onSubmit={handleSendMessage}>
                    <input
                      type="text"
                      placeholder="Write a message..."
                      value={messageText}
                      onChange={(event) => setMessageText(event.target.value)}
                      disabled={isSendingMessage || Boolean(activeConversation?.id && activeConversation.status === 'closed')}
                    />
                    <div className="chat-actions">
                      <label
                        className={`chat-upload-button ${isSendingMessage || activeConversation?.status === 'closed' ? 'disabled' : ''}`}
                        aria-label="Add attachments"
                      >
                        <FaPlus />
                        <input
                          ref={attachmentInputRef}
                          type="file"
                          multiple
                          disabled={isSendingMessage || Boolean(activeConversation?.id && activeConversation.status === 'closed')}
                          onChange={(event) => setAttachments(Array.from(event.target.files || []))}
                        />
                      </label>
                      {attachments.length > 0 && (
                        <span className="chat-attachment-count">{attachments.length} file{attachments.length > 1 ? 's' : ''}</span>
                      )}
                      <button type="submit" disabled={isSendingMessage || activeConversation?.status === 'closed'}>
                        {isSendingMessage ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            )}

            {tab === 'billing' && (
              <div className="section-card">
                <h3>VixPremium subscription</h3>
                <p className="muted-text">
                  Your VixPremium access switches automatically based on the PayPal subscription status.
                </p>
                {billingFeedback ? <p className="success-text">{billingFeedback}</p> : null}
                <div className="dashboard-grid dashboard-grid-tight">
                  <div className="stat-card">
                    <p className="stat-label">Current plan</p>
                    <h3>{hasActivePremium ? 'VixPremium' : 'Free'}</h3>
                    <p className="muted-text">
                      {hasActivePremium
                        ? `Up to ${user?.testerStrategyLimit || 100} saved strategies with MQ5 export and Community access.`
                        : `Up to ${user?.testerStrategyLimit || 1} saved strategy. Three MQ5 exports per month are included; Community posting and VixAI require VixPremium.`}
                    </p>
                  </div>
                  <div className="stat-card">
                    <p className="stat-label">Subscription status</p>
                    <h3>{currentSubscription?.status || 'Not linked'}</h3>
                    <p className="muted-text">
                      {currentSubscription?.providerSubscriptionId
                        ? `PayPal subscription ${currentSubscription.providerSubscriptionId}`
                        : 'No PayPal VixPremium subscription is currently linked to this user.'}
                    </p>
                  </div>
                </div>
                <div className="billing-panel">
                  <div className="billing-panel-copy">
                    <h4>{premiumPlan?.name || 'VixPremium'}</h4>
                    <p className="muted-text">
                      {premiumPlan?.description || 'Unlock more saved strategies, MQ5 EA source export, and premium community access.'}
                    </p>
                    <p className="muted-text">
                      {premiumPlan
                        ? `${premiumPriceLabel} · ${premiumPlan.strategyLimit} strategy saves · ${premiumPlan.canExportMq5 ? 'MQ5 export included' : 'No MQ5 export'} · Community access`
                        : 'Premium plan details are loading.'}
                    </p>
                    {currentSubscription?.nextBillingAt ? (
                      <p className="muted-text">
                        Next billing: {new Date(currentSubscription.nextBillingAt).toLocaleString()}
                      </p>
                    ) : null}
                  </div>
                  <div className="billing-panel-actions">
                    <div className="inline-actions">
                      <button
                        type="button"
                        className="ghost-button compact-button"
                        onClick={() => refreshBillingProfile({ syncRemote: true })}
                        disabled={billingLoading || billingAction === 'subscribe' || billingAction === 'cancel'}
                      >
                        {billingLoading ? 'Refreshing...' : 'Refresh status'}
                      </button>
                      {currentSubscription?.providerSubscriptionId && currentSubscription?.status === 'ACTIVE' ? (
                        <button
                          type="button"
                          className="ghost-button compact-button"
                          onClick={handleCancelSubscription}
                          disabled={billingAction === 'cancel'}
                        >
                          {billingAction === 'cancel' ? 'Cancelling...' : 'Cancel subscription'}
                        </button>
                      ) : null}
                    </div>
                    {!hasActivePremium ? (
                      <PayPalSubscriptionCard
                        planId={premiumPlan?.paypalPlanId}
                        priceLabel={premiumPriceLabel}
                        trialDays={premiumPlan?.trialDays || 0}
                        disabled={billingAction === 'subscribe' || billingLoading || !premiumPlan?.enabled}
                        onApprove={handleApproveSubscription}
                        onError={() => setError('PayPal could not start the subscription flow.')}
                        onCancel={() => setBillingFeedback('PayPal checkout was cancelled.')}
                      />
                    ) : (
                      <p className="muted-text">
                        {currentSubscription?.status && currentSubscription.status !== 'ACTIVE' && currentSubscription?.accessExpiresAt
                          ? `Your VixPremium subscription is cancelled and will not renew. You keep premium access until ${new Date(currentSubscription.accessExpiresAt).toLocaleString()}.`
                          : 'VixPremium access is active. If PayPal status changes, this dashboard, Builder limits, and Community access will update automatically.'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === 'settings' && (
              <>
              <EmailSubscriptionsPanel />
              <div className="section-card">
                <h3>Profile settings</h3>
              <p className="muted-text">Update your info or delete your account.</p>
              <div className="form-grid">
                <div className="form-grid two-column">
                  <div className="form-group">
                    <label htmlFor="profile-name">Full name</label>
                    <input
                      id="profile-name"
                      type="text"
                      placeholder="Your name"
                      value={profileName}
                      onChange={(event) => setProfileName(event.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="profile-email">Email</label>
                    <input id="profile-email" type="email" placeholder="you@domain.com" value={user?.email || ''} disabled />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" onClick={handleProfileSave}>Save changes</button>
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={() => {
                      setShowPasswordForm((previous) => !previous);
                      setPasswordFeedback('');
                      setError('');
                    }}
                  >
                    {showPasswordForm ? 'Close password form' : 'Change password'}
                  </button>
                </div>
              </div>
              {passwordFeedback ? <p className="success-text">{passwordFeedback}</p> : null}
              {showPasswordForm ? (
                <form className="password-change-panel" onSubmit={handlePasswordChange}>
                  <div className="form-grid two-column">
                    <div className="form-group">
                      <label htmlFor="current-password">Current password</label>
                      <input
                        id="current-password"
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(event) => setPasswordForm((current) => ({ ...current, currentPassword: event.target.value }))}
                        placeholder="Required for password accounts"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="new-password">New password</label>
                      <input
                        id="new-password"
                        type="password"
                        minLength={8}
                        value={passwordForm.newPassword}
                        onChange={(event) => setPasswordForm((current) => ({ ...current, newPassword: event.target.value }))}
                        placeholder="At least 8 characters"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="confirm-new-password">Confirm new password</label>
                      <input
                        id="confirm-new-password"
                        type="password"
                        minLength={8}
                        value={passwordForm.confirmPassword}
                        onChange={(event) => setPasswordForm((current) => ({ ...current, confirmPassword: event.target.value }))}
                        placeholder="Repeat new password"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" disabled={passwordPending}>
                      {passwordPending ? 'Updating...' : 'Update password'}
                    </button>
                  </div>
                </form>
              ) : null}
              <div className="danger-zone">
                <h4>Delete account</h4>
                <p className="muted-text">
                  This will remove your profile. Purchase records remain for compliance.
                </p>
                <button type="button" className="danger-button" onClick={handleDeleteAccount}>Delete account</button>
              </div>
            </div>
              </>
          )}
        </section>
        {dashboardConfirm && (
          <div className="dashboard-modal-overlay" role="presentation" onClick={closeDashboardConfirm}>
            <div className={`dashboard-confirm-modal ${dashboardConfirm.tone || ''}`} role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
              <div className="dashboard-confirm-header">
                <h3>{dashboardConfirm.title}</h3>
                <button type="button" onClick={closeDashboardConfirm}>x</button>
              </div>
              <p>{dashboardConfirm.message}</p>
              <div className="dashboard-confirm-actions">
                <button type="button" className="ghost-button" onClick={closeDashboardConfirm}>{dashboardConfirm.cancelLabel}</button>
                <button
                  type="button"
                  className={`primary-cta compact-button ${dashboardConfirm.tone === 'danger' ? 'danger' : ''}`}
                  onClick={() => {
                    if (typeof dashboardConfirm.onConfirm === 'function') {
                      dashboardConfirm.onConfirm();
                    } else {
                      closeDashboardConfirm();
                    }
                  }}
                >
                  {dashboardConfirm.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserDashboard;

